// 261005 E2-P2a — dòng hội thoại: tin người/trợ lý, nhãn, khối thay đổi (Xem thử → thẻ trước/sau → Áp/Bỏ), nút hành động, chi phí.
// Mọi quyết định hiển thị lấy từ `assistantView.ts` / `episodeFixUtils.ts`; file này chỉ vẽ.
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Collapse, Popconfirm, Spin, Tooltip, Typography } from 'antd';
import Button from '../WbButton';
import type { AssistantAction, AssistantBlock, AssistantInteraction } from '../../../../types/adaptationAssistant';
import { newLinesByScene } from '../../episodeFixUtils';
import { humanizeCodes } from '../../adaptationUtils';
import { actionView, blockCardView, disabledTip, groupChips, interactionTag, interruptedDetail, interruptedText, isStaleRunning, showInteractionTag, visibleAssistantMessages } from './assistantView';
import BeatDiff from './BeatDiff';
import { BlockBody, BlockFoot, BlockShell } from './BlockShell';
import AutoApplyLine from './AutoApplyLine';
import { Dot } from '../WbSlot';
import SkeletonBlockCard from './SkeletonBlockCard';
import SplitBlockCard from './SplitBlockCard';
import SourceBlockCard from './SourceBlockCard';
import LocalizeBlockCard from './LocalizeBlockCard';

const { Text } = Typography;

const LONG_TEXT = 1200;
/** Câu giữ chỗ BE lưu khi người dùng chỉ tick lỗi, không gõ chữ. */
const ONLY_CHIPS_TEXT = '(chỉ tick lỗi)';

const LongText: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  const long = text.length > LONG_TEXT;
  return (
    <>
      <span style={{ whiteSpace: 'pre-wrap' }}>{long && !open ? `${text.slice(0, LONG_TEXT)}…` : text}</span>
      {long && <Button type="link" size="small" onClick={() => setOpen((o) => !o)}>{open ? 'Thu lại' : 'Xem thêm'}</Button>}
    </>
  );
};

export interface TimelineHandlers {
  onPreview: (b: AssistantBlock) => void;
  onApply: (b: AssistantBlock) => void;
  /** E2-P3: "Khôi phục bản trước" (APPLIED) / "Áp lại" (RESTORED). */
  onUndo: (b: AssistantBlock) => void;
  onReapply: (b: AssistantBlock) => void;
  onDismiss: (b: AssistantBlock) => void;
  onExecute: (a: AssistantAction) => void;
}

interface Props extends TimelineHandlers {
  interactions: AssistantInteraction[];
  nextEpisode: number;
  /** `modifiedAt` hiện tại của tập — để bật/tắt "Khôi phục bản trước". */
  headModifiedAt?: string | null;
  /** `ctx.busy` hoặc khung đang thao tác: khoá nút ghi. */
  busy: boolean;
  /** Khối/hành động đang gọi (khoá riêng + spinner). */
  acting: string | null;
  /** E4-P1 — lý do hiện ở nút ghi bị tắt khi `busy` do sửa tay ("Đang sửa tay — Lưu hoặc Huỷ trước"). */
  busyReason?: string | null;
  /** ② : tên theo id cho dòng ảnh hưởng. */
  nameOf?: (id: string) => string;
  /** E5 — nhãn người đọc của chip lỗi theo id (để bong bóng lượt "chỉ tick lỗi" hiện nhãn thay vì câu giữ chỗ của BE). */
  chipLabels?: Record<string, string>;
  /** F19 — mốc giờ hiện tại (đẩy từ khung, cập nhật định kỳ) để nhận ra lượt RUNNING bị kẹt; "Gửi lại" đưa chữ lượt đó về ô soạn. */
  now?: number;
  onRetry?: (it: AssistantInteraction) => void;
}

const BlockCard: React.FC<{ block: AssistantBlock; nextEpisode: number; headModifiedAt?: string | null; busy: boolean; acting: string | null; busyReason?: string | null } & TimelineHandlers> = ({
  block, nextEpisode, headModifiedAt, busy, acting, busyReason, onPreview, onApply, onUndo, onReapply, onDismiss,
}) => {
  const v = blockCardView(block, nextEpisode, busy || !!acting, headModifiedAt, busyReason);
  const why = busy && busyReason ? busyReason : undefined;
  const p = block.diff;
  return (
    <BlockShell>
      <BlockBody title={block.title} tone={v.tone} stateLabel={v.phase !== 'proposed' ? v.headline : null} running={v.phase === 'running'}>
      {v.detail && <div style={{ fontSize: 13, color: 'var(--wb-text-2)', margin: '4px 0', wordBreak: 'break-word' }}>{v.detail}</div>}
      <AutoApplyLine note={v.autoNote} />
      {v.phase === 'failed' && v.errorText && <Alert style={{ marginTop: 6 }} type="error" showIcon message="Xem thử lỗi" description={v.errorText} />}
      {v.phase === 'ready' && p && (
        <>
          <div className="wb-ai-meta">{v.criticLabel} · {v.violationsLabel} · {v.wordsLabel}</div>
          {v.warnings.map((w, i) => <Alert key={i} style={{ marginBottom: 6 }} type={v.tone === 'danger' && i === 0 ? 'error' : 'warning'} showIcon message={w} />)}
          {(p.criticalLines ?? []).length > 0 && (
            <Collapse ghost size="small" items={[{ key: 'c', label: `${p.criticalLines?.length} lỗi nặng critic nêu (tham khảo)`, children: (p.criticalLines ?? []).map((l, i) => <div key={i} style={{ fontSize: 13 }}>{l}</div>) }]} />
          )}
          {(p.newLineSpeakers ?? []).length > 0 && (
            <Collapse ghost size="small" items={[{
              key: 'n', label: 'Câu mới — soát đúng người nói chưa',
              children: newLinesByScene(p.newLineSpeakers ?? []).map((g) => (
                <div key={g.scenePlanId} style={{ marginBottom: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{g.scenePlanId}</Text>
                  {g.lines.map((l, i) => <div key={i} style={{ fontSize: 13, wordBreak: 'break-word' }}>{l}</div>)}
                </div>
              )),
            }]} />
          )}
          <Collapse
            ghost size="small"
            items={(p.sceneChanges ?? []).map((c) => ({
              key: c.scenePlanId, label: `Trước/sau cảnh ${c.index}`,
              children: <div className="wb-aip-diff"><BeatDiff before={c.before} after={c.after} /></div>,
            }))}
          />
        </>
      )}
      </BlockBody>
      <BlockFoot>
        {v.canPreview && <Button size="small" type="text" loading={acting === `preview-${block.id}`} onClick={() => onPreview(block)}>{v.previewLabel}</Button>}
        {!v.canPreview && why && (v.phase === 'proposed' || v.phase === 'restored' || v.phase === 'failed' || v.phase === 'stale') && (
          <Tooltip title={why}><Button size="small" type="text" disabled>{v.previewLabel}</Button></Tooltip>
        )}
        {v.phase === 'ready' && (
          <Popconfirm
            title={v.tone === 'danger' ? 'Critic/kiểm tra báo có vấn đề — vẫn áp?' : 'Áp bản sửa này?'}
            description="Kịch bản của tập được ghi đè; bản cũ vẫn còn ở Phiên bản."
            okText="Áp" cancelText="Thôi" onConfirm={() => onApply(block)} disabled={!v.canApply}
          >
            <Tooltip title={disabledTip(v.canApply, why)}><Button size="small" type="primary" danger={v.tone === 'danger'} disabled={!v.canApply} loading={acting === `apply-${block.id}`}>Áp</Button></Tooltip>
          </Popconfirm>
        )}
        {block.state === 'APPLIED' && (
          <Tooltip title={v.undoReason ?? undefined}>
            <span>
              <Popconfirm
                title="Khôi phục bản trước? Kịch bản quay về bản trước khi áp đề xuất này."
                description="Bản hiện tại vẫn còn ở mục Phiên bản."
                okText="Khôi phục" cancelText="Thôi" onConfirm={() => onUndo(block)} disabled={!v.canUndo}
              >
                <Button size="small" type="text" disabled={!v.canUndo} loading={acting === `undo-${block.id}`}>Khôi phục bản trước</Button>
              </Popconfirm>
            </span>
          </Tooltip>
        )}
        {block.state === 'RESTORED' && (
          <Popconfirm
            title="Áp lại đề xuất này?" description="Không tốn AI; chỉ áp được khi kịch bản chưa khác bản gốc của đề xuất."
            okText="Áp lại" cancelText="Thôi" onConfirm={() => onReapply(block)} disabled={!v.canReapply}
          >
            <Tooltip title={disabledTip(v.canReapply, why)}><Button size="small" type="primary" disabled={!v.canReapply} loading={acting === `reapply-${block.id}`}>Áp lại</Button></Tooltip>
          </Popconfirm>
        )}
        {v.canDismiss && <Button size="small" type="text" loading={acting === `dismiss-${block.id}`} onClick={() => onDismiss(block)}>Bỏ</Button>}
      </BlockFoot>
    </BlockShell>
  );
};

const ActionButton: React.FC<{ action: AssistantAction; busy: boolean; acting: string | null; busyReason?: string | null; onExecute: (a: AssistantAction) => void }> = ({ action, busy, acting, busyReason, onExecute }) => {
  const v = actionView(action, busy || !!acting);
  if (v.doneText) return <Text type="secondary" style={{ fontSize: 13 }}>{v.label}: {v.doneText}</Text>;
  const btn = (
    <Button
      className="wb-aip-action" size="small" disabled={!v.enabled} loading={acting === `action-${action.id}`}
      onClick={v.confirm ? undefined : () => onExecute(action)}
    >
      <span className="wb-aip-action-label">{v.label}</span>
      {v.costTag !== 'Miễn phí' && <span className="wb-aip-action-cost"> · tốn AI</span>}
    </Button>
  );
  const tipped = !v.enabled && busy && busyReason && action.cost !== 'free' ? <Tooltip title={busyReason}>{btn}</Tooltip> : btn;
  if (!v.confirm) return tipped;
  return (
    <Popconfirm title={`${v.costTag} — chạy "${v.label}"?`} description="Việc này tốn AI; chỉ chạy khi bạn xác nhận." okText="Chạy" cancelText="Thôi" onConfirm={() => onExecute(action)} disabled={!v.enabled}>
      {tipped}
    </Popconfirm>
  );
};

const AssistantTimeline: React.FC<Props> = ({ interactions, nextEpisode, headModifiedAt, busy, acting, busyReason, nameOf, chipLabels, now, onRetry, onPreview, onApply, onUndo, onReapply, onDismiss, onExecute }) => {
  const endRef = useRef<HTMLDivElement | null>(null);
  const interruptedLine = (it: AssistantInteraction) => (
    <div className="wb-ai-err">
      <Dot tone={it.status === 'FAILED' ? 'bad' : 'warn'} /><span title={interruptedDetail(it) ? `Chi tiết kỹ thuật: ${interruptedDetail(it)}` : undefined}>{interruptedText(it)}</span>
      {onRetry && <Button size="small" type="link" className="btn-ai-retry" onClick={() => onRetry(it)}>Gửi lại</Button>}
    </div>
  );
  const sig = `${interactions.length}:${interactions[interactions.length - 1]?.status ?? ''}`;
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [sig]);
  return (
    <div className="wb-ai-timeline">
      {interactions.map((it) => {
        const tag = interactionTag(it);
        return (
          <div key={it.id} className="wb-ai-turn">
            {it.messages.filter((m) => m.role === 'user').map((m) => (
              <div key={m.id} className="wb-ai-user">
                {m.text.trim() === ONLY_CHIPS_TEXT ? (
                  <span className="wb-chips">
                    {groupChips((it.issueIds ?? []).map((id) => ({ id, label: chipLabels?.[id] ?? 'Lỗi đã gắn' }))).map((g) => <span key={g.ids.join('|')} className="wb-chip" title={g.title}><Dot tone="warn" />{g.label}</span>)}
                  </span>
                ) : <LongText text={m.text} />}
              </div>
            ))}
            {it.status === 'RUNNING' && !isStaleRunning(it, now) && <div className="wb-ai-err"><Spin size="small" /><span>Đang chờ AI trả lời…</span></div>}
            {it.status === 'RUNNING' && isStaleRunning(it, now) && interruptedLine(it)}
            {visibleAssistantMessages(it).map((m) => (
              <div key={m.id}>
                {tag && showInteractionTag(it) && <div className="wb-ai-err" style={{ marginBottom: 4 }}><Dot tone={tag === 'AI lỗi' ? 'bad' : 'warn'} />{tag}</div>}
                <div className="wb-ai-botrow"><span className="wb-ai-avatar" aria-hidden="true">✦</span><p className="wb-ai-bot"><LongText text={humanizeCodes(m.text, nameOf)} /></p></div>
              </div>
            ))}
            {it.status === 'FAILED' && interruptedLine(it)}
            {it.blocks.map((b) => (b.stageKey === 'source' ? (
              <SourceBlockCard key={b.id} block={b} headModifiedAt={headModifiedAt} busy={busy} acting={acting} busyReason={busyReason} onPreview={onPreview} onApply={onApply} onUndo={onUndo} onReapply={onReapply} onDismiss={onDismiss} onExecute={onExecute} />
            ) : b.stageKey === 'localize' ? (
              <LocalizeBlockCard key={b.id} block={b} headModifiedAt={headModifiedAt} busy={busy} acting={acting} busyReason={busyReason} onPreview={onPreview} onApply={onApply} onUndo={onUndo} onReapply={onReapply} onDismiss={onDismiss} onExecute={onExecute} />
            ) : b.stageKey === 'split' ? (
              <SplitBlockCard key={b.id} block={b} headModifiedAt={headModifiedAt} busy={busy} acting={acting} busyReason={busyReason} onPreview={onPreview} onApply={onApply} onUndo={onUndo} onReapply={onReapply} onDismiss={onDismiss} onExecute={onExecute} />
            ) : b.stageKey === 'skeleton' ? (
              <SkeletonBlockCard key={b.id} block={b} headModifiedAt={headModifiedAt} busy={busy} acting={acting} busyReason={busyReason} nameOf={nameOf} onPreview={onPreview} onApply={onApply} onUndo={onUndo} onReapply={onReapply} onDismiss={onDismiss} onExecute={onExecute} />
            ) : (
              <BlockCard key={b.id} block={b} nextEpisode={nextEpisode} headModifiedAt={headModifiedAt} busy={busy} acting={acting} busyReason={busyReason} onPreview={onPreview} onApply={onApply} onUndo={onUndo} onReapply={onReapply} onDismiss={onDismiss} onExecute={onExecute} />
            )))}
            {it.actions.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: '100%' }}>
                {it.actions.map((a) => <ActionButton key={a.id} action={a} busy={busy} acting={acting} busyReason={busyReason} onExecute={onExecute} />)}
              </div>
            )}
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
};

export default AssistantTimeline;
